import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type BridgeCreatedItem,
  type BridgeDataset,
  type BridgeDatasetItemDetail,
  type BridgeTraceDatasetMembership,
  type BridgeTraceDetail,
  type BridgeTraceSummary,
  langfuseBridgeService,
} from '@/services/langfuseBridge.service';
import { queryKeys } from '@/services/query.config';

/** Eval traces in Langfuse for a given call id (enabled when the modal opens). */
export function useLangfuseTraces(callId: string | null, enabled: boolean) {
  return useQuery<BridgeTraceSummary[]>({
    queryKey: queryKeys.langfuseBridge.tracesByCall(callId ?? ''),
    queryFn: () => langfuseBridgeService.listTracesByCallId(callId!),
    enabled: enabled && !!callId,
  });
}

/**
 * Full input/output of one trace, loaded lazily once a trace is selected.
 *
 * The queryFn deliberately does not consume `signal`, so switching to another
 * trace does not abort this request — it runs to completion and caches, letting
 * the reviewer flip back to an already-fetched trace instantly (and avoiding
 * spurious AbortErrors from rapid switching). Same rationale for the queries below.
 */
export function useLangfuseTrace(traceId: string | null) {
  return useQuery<BridgeTraceDetail>({
    queryKey: queryKeys.langfuseBridge.trace(traceId ?? ''),
    queryFn: () => langfuseBridgeService.getTrace(traceId!),
    enabled: !!traceId,
  });
}

/** Datasets a trace is already a member of (shown before the user adds again). */
export function useTraceDatasetMemberships(traceId: string | null, enabled: boolean) {
  return useQuery<BridgeTraceDatasetMembership[]>({
    queryKey: queryKeys.langfuseBridge.traceMemberships(traceId ?? ''),
    queryFn: () => langfuseBridgeService.listTraceDatasetMemberships(traceId!),
    enabled: enabled && !!traceId,
  });
}

/** All Langfuse datasets for the picker (loaded when the modal opens). */
export function useLangfuseDatasets(enabled: boolean) {
  return useQuery<BridgeDataset[]>({
    queryKey: queryKeys.langfuseBridge.datasets(),
    queryFn: () => langfuseBridgeService.listDatasets(),
    enabled,
  });
}

/** The existing item's expected output, to seed the editor when updating in place. */
export function useDatasetItem(datasetId: string | null, itemId: string | null, enabled: boolean) {
  return useQuery<BridgeDatasetItemDetail>({
    queryKey: queryKeys.langfuseBridge.datasetItem(datasetId ?? '', itemId ?? ''),
    queryFn: () => langfuseBridgeService.getDatasetItem(datasetId!, itemId!),
    enabled: enabled && !!datasetId && !!itemId,
  });
}

/** After a write/delete, membership + dataset lists change — refresh both for a trace. */
function invalidateTrace(
  queryClient: ReturnType<typeof useQueryClient>,
  sourceTraceId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.langfuseBridge.datasets() });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.langfuseBridge.traceMemberships(sourceTraceId),
  });
}

/** Create a dataset item from a trace + edited expected output. */
export function useCreateDatasetItem() {
  const queryClient = useQueryClient();
  return useMutation<
    BridgeCreatedItem,
    Error,
    { datasetId: string; sourceTraceId: string; expectedOutput: unknown }
  >({
    mutationFn: ({ datasetId, sourceTraceId, expectedOutput }) =>
      langfuseBridgeService.createDatasetItem(datasetId, {
        source_trace_id: sourceTraceId,
        expected_output: expectedOutput,
      }),
    onSuccess: (_data, { sourceTraceId }) => invalidateTrace(queryClient, sourceTraceId),
  });
}

/** Update (upsert) an existing dataset item's expected output. */
export function useUpdateDatasetItem() {
  const queryClient = useQueryClient();
  return useMutation<
    BridgeCreatedItem,
    Error,
    { datasetId: string; itemId: string; sourceTraceId: string; expectedOutput: unknown }
  >({
    mutationFn: ({ datasetId, itemId, sourceTraceId, expectedOutput }) =>
      langfuseBridgeService.updateDatasetItem(datasetId, itemId, {
        source_trace_id: sourceTraceId,
        expected_output: expectedOutput,
      }),
    onSuccess: (_data, { datasetId, itemId, sourceTraceId }) => {
      invalidateTrace(queryClient, sourceTraceId);
      // The cached item still holds the pre-update expected output — refresh it
      // so reopening the modal shows the new value, not stale data.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.langfuseBridge.datasetItem(datasetId, itemId),
      });
    },
  });
}

/** Remove a dataset item (delete). `sourceTraceId` is only used to refresh caches. */
export function useDeleteDatasetItem() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { datasetId: string; itemId: string; sourceTraceId: string }>({
    mutationFn: ({ datasetId, itemId }) =>
      langfuseBridgeService.deleteDatasetItem(datasetId, itemId),
    onSuccess: (_data, { datasetId, itemId, sourceTraceId }) => {
      invalidateTrace(queryClient, sourceTraceId);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.langfuseBridge.datasetItem(datasetId, itemId),
      });
    },
  });
}
